<template>
  <div class="users-info p-4">
    <ConfirmDialog />
    <div v-if="isLoading" class="text-center py-8">
      <p>Loading users...</p>
    </div>
    <div v-else-if="error" class="text-center py-8 text-red-500">
      <p>Error loading users: {{ error }}</p>
    </div>
    <div v-else>
      <div class="flex gap-4 mb-4">
        <div class="user-summary bg-white p-4 rounded-lg shadow-sm flex-1">
          <div class="flex justify-between mb-2">
            <span class="text-gray-600">Total users</span>
            <span class="font-bold">{{ users.length }}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600">Admins</span>
            <span class="font-bold text-purple-600">{{ adminUsersCount }}</span>
          </div>
        </div>
      </div>
      <div class="flex justify-start mb-4">
        <CreateUserDialog :tenant="tenant"@userCreated="handleUserCreated"
          buttonLabel="Add User"buttonIcon="pi pi-user-plus"
          buttonClass="bg-blue-900 hover:bg-blue-800 text-white border-blue-900 py-3 px-6" />
      </div>

      <EditUserDialog :tenant="tenant"
        :user="selectedUser"
        v-model:visible="editDialogVisible"
        @userUpdated="handleUserUpdated"
      />

      <UserDetailsModal
        :tenant="tenant"
        :userId="selectedUserId"
        v-model:visible="detailsDialogVisible"
      />

      <!-- Users Table with Filtering -->
      <div class="card">
        <DataTable 
          :value="users" 
          :paginator="true" 
          :rows="100"
          :rowsPerPageOptions="[25, 50, 100, 200]"
          filterDisplay="menu"
          :globalFilterFields="globalFilterFields"
          v-model:filters="filters"
          :loading="isLoading"
          responsiveLayout="scroll" 
          class="users-table"
        >
          <template #header>
            <div class="flex justify-between items-center">
              <span class="p-input-icon-left">
                <i class="pi pi-search" />
                <InputText 
                  v-model="filters['global'].value" 
                  placeholder="Search users..." 
                  class="p-3"
                />
              </span>
              <div class="flex gap-2">
                <span class="text-sm text-gray-600 self-center">
                  {{ users.length }} user{{ users.length !== 1 ? 's' : '' }} total
                </span>
              </div>
            </div>
          </template>
          
          <Column field="first_name" header="First Name" :sortable="true">
            <template #body="slotProps">
              {{ slotProps.data.first_name || 'N/A' }}
            </template>
          </Column>
          
          <Column field="last_name" header="Last Name" :sortable="true">
            <template #body="slotProps">
              {{ slotProps.data.last_name || 'N/A' }}
            </template>
          </Column>
          
          <Column field="email" header="Email" :sortable="true">
            <template #body="slotProps">
              {{ slotProps.data.email || 'N/A' }}
            </template>
          </Column>
          
          <Column field="phone_number" header="Phone Number" :sortable="true">
            <template #body="slotProps">
              {{ slotProps.data.phone_number || 'N/A' }}
            </template>
          </Column>
          
          <Column field="role_name" header="Role" :sortable="true">
            <template #body="slotProps">
              <Tag :value="slotProps.data.role_name || slotProps.data.role || 'N/A'" severity="info" />
            </template>
          </Column>
          
          <Column header="Actions" :style="{ width: '5rem' }">
            <template #body="{ data }">
              <Menu ref="menu" :model="currentMenuItems" :popup="true" />
              <Button 
                icon="pi pi-ellipsis-h" 
                class="p-button-rounded p-button-text p-button-plain" 
                @click="(event) => toggleMenu(event, data)"
                aria-label="Options"
              />
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { FilterMatchMode } from '@primevue/core/api';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'vue-toastification';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Menu from 'primevue/menu';
import ConfirmDialog from 'primevue/confirmdialog';
import CreateUserDialog from './CreateUserDialog.vue';
import EditUserDialog from './EditUserDialog.vue';
import UserDetailsModal from './UserDetailsModal.vue';
import axios from 'axios';

const props = defineProps({
  tenant: {
    type: String,
    required: true
  }
});

const users = ref([]);
const isLoading = ref(false);
const error = ref(null);
const menu = ref(); // Reference to the Menu component
const confirm = useConfirm();
const toast = useToast();

// Edit dialog state
const editDialogVisible = ref(false);
const selectedUser = ref(null);

// Details dialog state
const detailsDialogVisible = ref(false);
const selectedUserId = ref(null);

// Menu state
const currentMenuUser = ref(null);

// Initialize filters
const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS }
});

// Define global filter fields
const globalFilterFields = ['first_name', 'last_name', 'email', 'phone_number', 'role_name', 'role'];

const fetchUsers = async () => {
  if (!props.tenant) {
    users.value = [];
    error.value = 'Tenant information is not provided.';
    return;
  }
  isLoading.value = true;
  error.value = null;
  try {
    const response = await axios.get('/api/v1/users_proxy', {
      params: { tenant: props.tenant }
    });
    // The proxy returns the data from the AlldoQ API
    if (response.data && response.data.data) {
      users.value = response.data.data;
    } else {
      users.value = response.data || [];
    }
  } catch (err) {
    console.error('Failed to fetch users:', err);
    if (err.response && err.response.data && err.response.data.error) {
      error.value = err.response.data.error;
    } else {
      error.value = 'An unexpected error occurred.';
    }
    users.value = [];
  }
  isLoading.value = false;
};

const adminUsersCount = computed(() => {
  // Assuming role_name 'admin' or role 'admin' signifies an admin user
  return users.value.filter(u => (u.role_name && u.role_name.toLowerCase() === 'admin') || (u.role && u.role.toLowerCase() === 'admin')).length;
});

// Computed property for current menu items
const currentMenuItems = computed(() => {
  if (!currentMenuUser.value) return [];
  return getActionItems(currentMenuUser.value);
});

// Handle user creation success
const handleUserCreated = (newUser) => {
  console.log('New user created:', newUser);
  // Refresh the users list to include the new user
  fetchUsers();
};

// Toggle dropdown menu
const toggleMenu = (event, user) => {
  currentMenuUser.value = user;
  menu.value.toggle(event);
};

// Get action items for dropdown menu
const getActionItems = (user) => {
  return [
    {
      label: 'View details',
      icon: 'pi pi-eye',
      command: () => handleViewDetails(user)
    },
    {
      label: 'Edit user',
      icon: 'pi pi-user-edit',
      command: () => handleEditUser(user)
    },
    {
      label: 'Re-issue invitation',
      icon: 'pi pi-check-circle',
      command: () => confirmUser(user)
    },
    {
      label: 'Delete user',
      icon: 'pi pi-trash',
      className: 'p-error',
      command: () => confirmDeleteUser(user)
    }
  ];
};

// Handle view details
const handleViewDetails = (user) => {
  selectedUserId.value = user.id;
  detailsDialogVisible.value = true;
};

// Handle edit user
const handleEditUser = (user) => {
  selectedUser.value = user;
  editDialogVisible.value = true;
};

// Handle user update success
const handleUserUpdated = (updatedUser) => {
  console.log('User updated:', updatedUser);
  // Refresh the users list to reflect the changes
  fetchUsers();
};

// Confirm user action
const confirmUser = (user) => {
  confirm.require({
    message: `Are you sure you want to confirm the account for ${user.email}?`,
    header: 'Confirm User Account',
    icon: 'pi pi-question-circle',
    acceptButtonProps: { class: 'p-button-success' },
    accept: () => confirmUserAccount(user),
    reject: () => {}
  });
};

// Confirm delete user
const confirmDeleteUser = (user) => {
  confirm.require({
    message: `Are you sure you want to delete ${user.email}? This action cannot be undone.`,
    header: 'Delete User',
    icon: 'pi pi-exclamation-triangle',
    acceptButtonProps: { class: 'p-button-danger' },
    accept: () => deleteUser(user),
    reject: () => {}
  });
};

// Confirm user account API call
const confirmUserAccount = async (user) => {
  try {
    isLoading.value = true;
    const response = await axios.post(`/api/v1/users_proxy/${user.id}/confirm`, {
      tenant: props.tenant
    });
    
    if (response.status === 200) {
      toast.success(`User ${user.email} confirmed successfully!`);
      fetchUsers(); // Refresh the list
    } else {
      toast.error('Failed to confirm user');
    }
  } catch (error) {
    console.error('Error confirming user:', error);
    if (error.response && error.response.data) {
      const errorData = error.response.data;
      if (errorData.error) {
        toast.error(errorData.error);
      } else {
        toast.error('Failed to confirm user');
      }
    } else {
      toast.error('An error occurred while confirming the user');
    }
  } finally {
    isLoading.value = false;
  }
};

// Delete user API call
const deleteUser = async (user) => {
  try {
    isLoading.value = true;
    const response = await axios.delete(`/api/v1/users_proxy/${user.id}`, {
      params: { tenant: props.tenant }
    });
    
    if (response.status === 200) {
      toast.success(`User ${user.email} deleted successfully!`);
      fetchUsers(); // Refresh the list
    } else {
      toast.error('Failed to delete user');
    }
  } catch (error) {
    console.error('Error deleting user:', error);
    if (error.response && error.response.data) {
      const errorData = error.response.data;
      if (errorData.error) {
        toast.error(errorData.error);
      } else {
        toast.error('Failed to delete user');
      }
    } else {
      toast.error('An error occurred while deleting the user');
    }
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchUsers();
});

watch(() => props.tenant, (newTenant, oldTenant) => {
  if (newTenant !== oldTenant) {
    fetchUsers();
  }
});
</script>

<style scoped>
.users-table :deep(.p-datatable-thead th) {
  background-color: #f9fafb;
  color: #374151;
  font-weight: 600;
}

.users-table :deep(.p-datatable-tbody > tr > td) {
  border-bottom: 1px solid #e5e7eb;
}

.users-table :deep(.p-tag) {
  font-size: 0.8rem;
  padding: 0.2rem 0.4rem;
}

/* Style action buttons */
.p-button-rounded.p-button-text {
  padding: 0.5rem !important;
}

/* Menu styling */
:deep(.p-menu) {
  width: 200px;
}

:deep(.p-menu .p-menuitem-link) {
  padding: 0.75rem 1rem;
}

:deep(.p-menu .p-menuitem-link .p-menuitem-icon) {
  margin-right: 0.75rem;
}

:deep(.p-menu .p-menuitem-link .p-menuitem-text) {
  font-size: 0.9rem;
}

:deep(.p-error .p-menuitem-text, .p-error .p-menuitem-icon) {
  color: #ef4444;
}

/* Search input styling */
.p-input-icon-left {
  padding: 0.5rem 0;
}

.p-input-icon-left i {
  color: #6b7280;
}

/* Card styling */
.card {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* DataTable header styling */
:deep(.p-datatable-header) {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem;
}

/* Search input focus */
:deep(.p-inputtext:focus) {
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
}

/* User count badge styling */
.text-sm.text-gray-600 {
  font-weight: 500;
  color: #6b7280;
}
</style>
